import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EmergencySection = ({ onScheduleMaintenance, onConsultation }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [availableTechnicians, setAvailableTechnicians] = useState(12);
  const [averageResponseTime, setAverageResponseTime] = useState(25);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      
      // Simulate dynamic technician availability
      const hour = new Date()?.getHours();
      if (hour >= 6 && hour <= 22) {
        setAvailableTechnicians(Math.floor(Math.random() * 8) + 8); // 8-15 during day
        setAverageResponseTime(Math.floor(Math.random() * 15) + 20); // 20-35 minutes
      } else {
        setAvailableTechnicians(Math.floor(Math.random() * 4) + 3); // 3-6 during night
        setAverageResponseTime(Math.floor(Math.random() * 20) + 30); // 30-50 minutes
      }
    }, 30000); // Update every 30 seconds

    return () => clearInterval(timer);
  }, []);

  const handleEmergencyCall = () => {
    window.location.href = 'tel:+6282226648077';
  };

  const handleEmergencyWhatsApp = () => {
    const message = `🚨 EMERGENCY AC SERVICE 🚨\n\nSaya butuh bantuan darurat untuk AC yang bermasalah:\n\n📍 Lokasi: [Isi alamat lengkap]\n⏰ Waktu: ${currentTime?.toLocaleString('id-ID')}\n🔥 Masalah: [Jelaskan masalah AC]\n\nMohon teknisi datang secepatnya. Terima kasih!`;
    window.open(`https://wa.me/6282226648077?text=${encodeURIComponent(message)}`, '_blank');
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const isBusinessHours = () => {
    const hour = currentTime?.getHours();
    return hour >= 6 && hour <= 22;
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-destructive/5 via-background to-accent/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6 animate-pulse">
            <Icon name="AlertTriangle" size={16} className="mr-2" />
            Layanan Darurat 24/7
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
            AC Rusak Mendadak? Kami Siap Membantu!
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Jangan panik jika AC tiba-tiba rusak. Tim emergency kami standby 24 jam 
            untuk mengatasi masalah AC darurat Anda dengan respon super cepat.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Emergency Info */}
          <div>
            {/* Current Status */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-border mb-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Clock" size={32} className="text-secondary" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-foreground mb-2">
                  Status Layanan Saat Ini
                </h3>
                <div className="text-lg text-muted-foreground">
                  {formatTime(currentTime)} WIB
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="text-center p-4 bg-secondary/5 rounded-xl">
                  <div className="text-3xl font-heading font-bold text-secondary mb-2">
                    {availableTechnicians}
                  </div>
                  <div className="text-sm text-muted-foreground">Teknisi Standby</div>
                  <div className="flex items-center justify-center mt-2">
                    <div className="w-2 h-2 bg-secondary rounded-full animate-pulse mr-2"></div>
                    <span className="text-xs text-secondary font-semibold">Online</span>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/5 rounded-xl">
                  <div className="text-3xl font-heading font-bold text-primary mb-2">
                    {averageResponseTime}
                  </div>
                  <div className="text-sm text-muted-foreground">Menit Respon</div>
                  <div className="text-xs text-primary font-semibold mt-2">
                    {isBusinessHours() ? 'Jam Kerja' : 'Layanan Malam'}
                  </div>
                </div>
              </div>
            </div>

            {/* Emergency Scenarios */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-border">
              <h3 className="font-heading font-bold text-xl text-foreground mb-6 flex items-center">
                <Icon name="AlertCircle" size={24} className="text-destructive mr-3" />
                Kapan Harus Panggil Emergency?
              </h3>
              
              <div className="space-y-4">
                {[
                  {
                    icon: "Flame",
                    title: "AC Mengeluarkan Asap/Bau Terbakar",
                    description: "Segera matikan AC dan hubungi kami",
                    priority: "Sangat Urgent"
                  },
                  {
                    icon: "Droplets",
                    title: "Kebocoran Air Berlebihan",
                    description: "Air AC bocor deras merusak furniture",
                    priority: "Urgent"
                  },
                  {
                    icon: "Zap",
                    title: "AC Konslet/Korsleting Listrik",
                    description: "Masalah kelistrikan yang berbahaya",
                    priority: "Sangat Urgent"
                  },
                  {
                    icon: "Volume2",
                    title: "Suara Sangat Berisik Tiba-tiba",
                    description: "Bunyi aneh yang mengganggu tetangga",
                    priority: "Perlu Segera"
                  }
                ]?.map((scenario, index) => (
                  <div key={index} className="flex items-start p-4 bg-muted/30 rounded-xl">
                    <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <Icon name={scenario?.icon} size={20} className="text-destructive" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground mb-1">{scenario?.title}</h4>
                      <p className="text-sm text-muted-foreground mb-2">{scenario?.description}</p>
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                        scenario?.priority === 'Sangat Urgent' ?'bg-destructive/10 text-destructive' 
                          : scenario?.priority === 'Urgent' ?'bg-accent/10 text-accent' :'bg-primary/10 text-primary'
                      }`}>
                        {scenario?.priority}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="sticky top-24">
            {/* Emergency CTA */}
            <div className="bg-gradient-to-br from-destructive/10 to-accent/10 rounded-2xl p-6 sm:p-8 border-2 border-destructive/20 mb-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Phone" size={40} className="text-destructive" />
                </div>
                <h3 className="font-heading font-bold text-2xl sm:text-3xl text-foreground mb-4">
                  Hubungi Emergency Sekarang!
                </h3>
                <p className="text-muted-foreground mb-6">
                  Tim emergency kami siap datang ke lokasi Anda dalam waktu {averageResponseTime} menit
                </p>
              </div>

              <div className="space-y-4">
                <Button
                  onClick={handleEmergencyCall}
                  size="lg"
                  fullWidth
                  className="bg-destructive hover:bg-destructive/90 text-white font-bold text-lg sm:text-xl py-4 shadow-cta"
                >
                  <div className="flex items-center justify-center w-full">
                    <Icon name="Phone" size={24} className="mr-3" />
                    <span>TELEPON DARURAT</span>
                  </div>
                </Button>

                <Button
                  onClick={handleEmergencyWhatsApp}
                  variant="outline"
                  size="lg"
                  fullWidth
                  className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-bold text-lg py-4"
                >
                  <Icon name="MessageCircle" size={24} className="mr-3" />
                  WhatsApp Emergency
                </Button>
              </div>

              <div className="mt-6 p-4 bg-white/50 rounded-xl">
                <div className="flex flex-wrap items-center justify-center text-center text-sm text-muted-foreground gap-x-2">
                  <Icon name="Shield" size={16} className="mr-2 text-secondary shrink-0" />
                  <span>Layanan 24/7 • Garansi Datang • Teknisi Bersertifikat</span>
                </div>
              </div>
            </div>

            {/* Service Areas */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-border">
              <h3 className="font-heading font-bold text-xl text-foreground mb-6 flex items-center">
                <Icon name="MapPin" size={24} className="text-primary mr-3" />
                Area Layanan Emergency
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {[
                  { area: "Jakarta Pusat", time: "15-25 min" },
                  { area: "Jakarta Selatan", time: "20-30 min" },
                  { area: "Jakarta Timur", time: "25-35 min" },
                  { area: "Jakarta Barat", time: "20-30 min" },
                  { area: "Jakarta Utara", time: "25-35 min" },
                  { area: "Depok/Bogor", time: "30-45 min" },
                  { area: "Tangerang", time: "25-40 min" },
                  { area: "Bekasi", time: "30-45 min" }
                ]?.map((location, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-muted/30 rounded-lg text-sm">
                    <span className="font-medium text-foreground">{location?.area}</span>
                    <span className="text-primary font-semibold">{location?.time}</span>
                  </div>
                ))}
              </div>

              <div className="bg-primary/5 rounded-xl p-4">
                <div className="flex items-start sm:items-center">
                  <Icon name="Info" size={16} className="text-primary mr-2 mt-1 sm:mt-0 shrink-0" />
                  <span className="text-sm text-foreground">
                    Waktu respon dapat bervariasi tergantung kondisi lalu lintas dan cuaca
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-card border border-border max-w-4xl mx-auto">
            <h3 className="font-heading font-bold text-2xl text-foreground mb-4">
              Jangan Tunggu Sampai AC Rusak Total!
            </h3>
            <p className="text-muted-foreground mb-6">
              Maintenance rutin dapat mencegah kerusakan mendadak. 
              Jadwalkan perawatan AC Anda sekarang dengan harga spesial.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="outline"
                size="lg"
                onClick={onScheduleMaintenance}
                className="font-semibold border-primary text-primary hover:bg-primary hover:text-white"
              >
                <Icon name="Calendar" size={20} className="mr-2" />
                Jadwalkan Maintenance
              </Button>
              <Button
                size="lg"
                onClick={onConsultation}
                className="bg-secondary hover:bg-secondary/90 font-semibold"
              >
                <Icon name="MessageCircle" size={20} className="mr-2" />
                Konsultasi Gratis
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmergencySection;